import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useUserAuth } from "../../context/userAuthContext";
import { ArrowLeft, Upload, X, Image as ImageIcon } from "lucide-react";

const NewCategory = () => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [categoryName, setCategoryName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Manejar la selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "image/svg+xml" && !file.type.startsWith("image/")) {
        setError("Please upload an image file (PNG, SVG, etc.)");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError("File size should be less than 5MB");
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError("");
    }
  };

  // Resetear la selección de imagen
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Manejar la creación de la categoría
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!categoryName.trim()) {
      setError("Please enter a category name");
      return;
    }

    if (!imageFile) {
      setError("Please upload a category icon");
      return;
    }

    setLoading(true);

    try {
      // Subir imagen a Storage
      const storageRef = ref(storage, `categories/${Date.now()}_${imageFile.name}`);
      const uploadResult = await uploadBytes(storageRef, imageFile);
      const imageUrl = await getDownloadURL(uploadResult.ref);

      // Crear documento en Firestore
      const categoryData = {
        categoryName: categoryName.trim(),
        categoryIcon: imageUrl,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
      };

      await addDoc(collection(db, "categories"), categoryData);
      navigate(-1);
    } catch (err) {
      console.error("Error creating category:", err);
      setError("Failed to create category. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between py-2 px-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-medium">Create New Category</h1>
          <div className="w-8" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-w-md mx-auto">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Category Name Input */}
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Category Name"
              className="w-full text-lg font-medium placeholder:text-gray-400 focus:outline-none"
              maxLength={50}
            />
          </div>

          {/* Image Upload */}
          <div className="bg-white p-4 rounded-xl shadow-sm">
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Category icon preview"
                  className="w-32 h-32 object-contain mx-auto"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="icon-upload"
                />
                <label
                  htmlFor="icon-upload"
                  className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-orange-500 transition-colors"
                >
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-500">
                    Upload Category Icon
                  </span>
                  <span className="text-xs text-gray-400">
                    PNG, SVG (max. 5MB)
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !categoryName.trim() || !imageFile}
            className="w-full bg-orange-500 text-white py-3 rounded-xl font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Creating..." : "Create Category"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewCategory;